import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import timeGridPlugin from '@fullcalendar/timegrid';
import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

// NOTE:
// FullCalendar v6 no longer exports CSS files via simple paths like `@fullcalendar/core/index.css`.
// The previous CSS imports caused build-time "Package path ./index.css is not exported" errors.
// We therefore rely on the default plugin styles bundled with the JS.
// If you later want custom styling, we can add scoped CSS classes around this component.

const Calendar = forwardRef(({ 
  selectedDate, 
  events = [], 
  eventContent,
  slotMinTime = '06:00:00',
  slotMaxTime = '22:00:00',
  allDaySlot = true,
  nowIndicator = false,
  height = 'auto',
  initialView: initialViewProp = 'timeGridWeek',
  /** When true, use a fixed key so the calendar does not remount when selectedDate changes (e.g. in overlay). Navigation is then done via API only. */
  stableKey = false,
  onEventClick,
  onDateClick,
  onTitleClick,
  onDatesSet,
}, ref) => {
  // Fallback to "today" if no date is provided
  const currentDate = selectedDate || new Date();
  const calendarRef = useRef(null);

  // Expose calendar API through ref (only if ref is provided)
  useImperativeHandle(ref, () => {
    if (!calendarRef.current) {
      return null;
    }
    return {
      getApi: () => calendarRef.current?.getApi() || null,
      getEl: () => {
        // FullCalendar ref doesn't have getEl, so we get the DOM element directly
        const api = calendarRef.current?.getApi();
        return api ? api.el : null;
      },
    };
  }, []);

  // Handle title click
  useEffect(() => {
    if (!onTitleClick) return;

    // Wait for calendar to render, then attach click handler
    const attachTitleClickHandler = () => {
      if (!calendarRef.current) return null;
      
      const calendarApi = calendarRef.current.getApi();
      if (!calendarApi || !calendarApi.el) return null;

      // Find the title element and make it clickable
      const titleElement = calendarApi.el.querySelector('.fc-toolbar-title');
      if (titleElement) {
        titleElement.style.cursor = 'pointer';
        titleElement.style.userSelect = 'none';
        
        const handleTitleClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (onTitleClick && calendarRef.current) {
            try {
              const api = calendarRef.current.getApi();
              if (api) {
                const currentDate = api.getDate();
                const currentView = api.view.type; // Get current view type
                onTitleClick(currentDate, currentView);
              }
            } catch (error) {
              console.warn('Error getting calendar date:', error);
            }
          }
        };

        titleElement.addEventListener('click', handleTitleClick);
        return () => {
          titleElement.removeEventListener('click', handleTitleClick);
        };
      }
      return null;
    };

    // Try immediately
    let cleanup = attachTitleClickHandler();
    
    // If not found, try again after a short delay (calendar might still be rendering)
    if (!cleanup) {
      const timeoutId = setTimeout(() => {
        cleanup = attachTitleClickHandler();
      }, 100);

      return () => {
        clearTimeout(timeoutId);
        if (cleanup) cleanup();
      };
    }

    return cleanup;
  }, [onTitleClick, currentDate]);

  return (
    <div className="w-full h-full p-4">
      <div className="w-full h-full min-h-[600px]">
        <FullCalendar
          ref={calendarRef}
          // Recreate the calendar when the selected date changes so that
          // the visible week always aligns with the chosen day.
          key={stableKey ? 'fc-calendar-stable' : `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView={initialViewProp}
          // Ensure weeks run Sunday → Saturday
          firstDay={0}
          initialDate={currentDate}
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,timeGridWeek,timeGridDay',
          }}
          height={height}
          events={events}
          eventContent={eventContent}
          slotMinTime={slotMinTime}
          slotMaxTime={slotMaxTime}
          allDaySlot={allDaySlot}
          nowIndicator={nowIndicator}
          eventClick={onEventClick}
          dateClick={onDateClick}
          datesSet={onDatesSet}
        />
      </div>
    </div>
  );
});

Calendar.displayName = 'Calendar';

export default Calendar;